import { c as _c } from "react/compiler-runtime";
import { useMemo } from 'react';
import { differenceInCalendarDays, format, isToday, isYesterday } from 'shared/dateFns';
import { useAuth } from 'shared/context/AuthContext';
import { useCopilot } from 'shared/context/CopilotContext';
import { useCurrentClient } from 'shared/context/ClientContext';
import { type ChatHistoryEntry, useGetChatHistory } from 'shared/copilot/api';
export type ChatHistoryBucketKey = 'TODAY' | 'YESTERDAY' | 'PREVIOUS_7_DAYS' | 'PREVIOUS_30_DAYS' | `MONTH:${string}`;
const getBucketKey = (date: Date, now: Date): ChatHistoryBucketKey => {
  if (isToday(date)) return 'TODAY';
  if (isYesterday(date)) return 'YESTERDAY';
  const days = differenceInCalendarDays(now, date);
  if (days <= 7) return 'PREVIOUS_7_DAYS';
  if (days <= 30) return 'PREVIOUS_30_DAYS';
  return `MONTH:${format(date, 'yyyy-MM')}`;
};

/**
 * Returns chat history grouped into time-based buckets for the history dropdown.
 *
 * Steps:
 * 1. Excludes the currently-open chat so it doesn't appear in the list.
 * 2. Sorts the remaining entries by `updatedAt` descending (most recent first).
 * 3. Buckets each entry by `getBucketKey`:
 *    `TODAY` → `YESTERDAY` → `PREVIOUS_7_DAYS` → `PREVIOUS_30_DAYS` →
 *    `MONTH:yyyy-MM` (for anything older).
 *
 * Sort order is preserved within each bucket because entries are appended in
 * order during the single forward pass.
 */
export const useGetChatHistoryGroupedByDate = options => {
  const $ = _c(7);
  const {
    currentClient
  } = useCurrentClient();
  const {
    currentUser
  } = useAuth();
  const isEnabled = options?.enabled ?? true;
  const {
    data: chatHistory,
    isPending,
    isError
  } = useGetChatHistory(currentClient.id, currentUser?.id, options);
  const {
    currentChatId
  } = useCopilot();
  let grouped;
  if ($[0] !== chatHistory || $[1] !== currentChatId) {
    const now = new Date();
    const sorted = chatHistory.filter(entry => entry.id !== currentChatId).sort(_temp);
    grouped = {};
    for (const entry_0 of sorted) {
      const key = getBucketKey(entry_0.updatedAt, now);
      if (!(key in grouped)) {
        grouped[key] = [];
      }
      grouped[key].push(entry_0);
    }
    $[0] = chatHistory;
    $[1] = currentChatId;
    $[2] = grouped;
  } else {
    grouped = $[2];
  }
  const data = grouped as Record<ChatHistoryBucketKey, ChatHistoryEntry[]>;
  const isLoading = isEnabled && isPending;
  let t0;
  if ($[3] !== data || $[4] !== isError || $[5] !== isLoading) {
    t0 = {
      data,
      isLoading,
      isError
    };
    $[3] = data;
    $[4] = isError;
    $[5] = isLoading;
    $[6] = t0;
  } else {
    t0 = $[6];
  }
  return t0;
};
function _temp(a, b) {
  return b.updatedAt.getTime() - a.updatedAt.getTime();
}